import store from '@/store';
import { getDayAsBytes } from '@/utils';
import {ResultCategory, Intake, SiteId, SpecialEventIds} from '@/enums';
import {GateAnswer, GateAnswerData, IntakeAmount, IntakeResult, PreQuestionList} from '@/types';
import { getIntake, getIntakeResult, getIntakeFeedback } from '@/utils/resultUtil';
import {isVariationMunicipality} from "@/utils/common";

type Portion = {
	itemId: number;
	portionIndex: number;
	count: number;
	days: number;
};

type Registration = {
	questionId: number;
	portions: Portion[];
};

type Gate = {
	gateId: number;
	days: number;
};

type IntakeData = {
	totalWeight: number;
	totalPortions: number;
	conclusion: ResultCategory;
	resultText: string;
};

type ExtraData = {
	preQuestions: PreQuestionList;
	specialEvents: SpecialEventData;
	sessionDuration: number;
};

type SpecialEventData = {
	[SpecialEventIds.BIRTHDAY]: boolean;
	[SpecialEventIds.GRANDPARENTS]: boolean;
	[SpecialEventIds.SOCIAL_EVENTS]: boolean;
	[SpecialEventIds.EXCURSIONS]: boolean;
	[SpecialEventIds.SLEEPOVER]: boolean;
	[SpecialEventIds.OTHER]: boolean;
};

type Result = {
	foodResult: IntakeData;
	drinkResult: IntakeData;
	totalConclusion: ResultCategory;
	extraData: ExtraData;
};

type PostData = {
	registrations: Registration[];
	gates: Gate[];
	result: Result;
};

export function postResult() {
	const postData: PostData = {
		registrations: [],
		gates: [],
		result: {
			foodResult: {
				totalWeight: 0,
				totalPortions: 0,
				conclusion: ResultCategory.GREEN,
				resultText: ''
			},
			drinkResult: {
				totalWeight: 0,
				totalPortions: 0,
				conclusion: ResultCategory.GREEN,
				resultText: ''
			},
			totalConclusion: ResultCategory.GREEN,
			extraData: {
				preQuestions: store.state.preQuestionList,
				specialEvents: {
					[SpecialEventIds.BIRTHDAY]: false,
					[SpecialEventIds.GRANDPARENTS]: false,
					[SpecialEventIds.SOCIAL_EVENTS]: false,
					[SpecialEventIds.EXCURSIONS]: false,
					[SpecialEventIds.SLEEPOVER]: false,
					[SpecialEventIds.OTHER]: false,
				},
				sessionDuration: 1000,
			}
		}
	};

	// Special events

	for (const key of store.state.specialEventsAnswers) {

		/* if (key in SpecialEventIds) {
			postData.result.extraData.specialEvents[key as keyof typeof SpecialEventIds] = true;
		}*/

		if (key === SpecialEventIds.BIRTHDAY) {
			postData.result.extraData.specialEvents[SpecialEventIds.BIRTHDAY] = true;
		}
		else if (key === SpecialEventIds.GRANDPARENTS) {
			postData.result.extraData.specialEvents[SpecialEventIds.GRANDPARENTS] = true;
		}
		else if (key === SpecialEventIds.SOCIAL_EVENTS) {
			postData.result.extraData.specialEvents[SpecialEventIds.SOCIAL_EVENTS] = true;
		}
		else if (key === SpecialEventIds.EXCURSIONS) {
			postData.result.extraData.specialEvents[SpecialEventIds.EXCURSIONS] = true;
		}
		else if (key === SpecialEventIds.SLEEPOVER) {
			postData.result.extraData.specialEvents[SpecialEventIds.SLEEPOVER] = true;
		}
		else if (key === SpecialEventIds.OTHER) {
			postData.result.extraData.specialEvents[SpecialEventIds.OTHER] = true;
		}
	}

	// Session duration
	let dateStart: Date = store.state.sessionDurationStartTime;
	let dateEnd: Date = store.state.sessionDurationEndTime;

	let differenceInMilliseconds = dateEnd.getTime() - dateStart.getTime();
	let differenceInSeconds = Math.round(differenceInMilliseconds / 1000);

	postData.result.extraData.sessionDuration = differenceInSeconds;

	// Registrations
	for (const input of store.state.inputs) {
		let registrationIndex = postData.registrations.findIndex((r) => r.questionId === input.questionId);

		if (registrationIndex < 0) {
			postData.registrations.push({
				questionId: input.questionId,
				portions: []
			});

			registrationIndex = postData.registrations.length - 1;
		}

		for (const day in input.selectedPortions) {
			//@ts-ignore
			const portionValue: number = input.selectedPortions[day].index;
			if (portionValue === -1) {
				continue;
			}

			// @ts-ignore
			let multiplier:number = input.selectedPortions[day].multiplier;
			//let portionIndex = postData.registrations[registrationIndex].portions.findIndex((p) => p.itemId === input.data.id && p.portionIndex === portionValue);

			//if (portionIndex < 0) {
			postData.registrations[registrationIndex].portions.push({
				itemId: input.data.id,
				portionIndex: portionValue,
				count: multiplier,
				days: getDayAsBytes(day)
			});
			//} else {
			//postData.registrations[registrationIndex].portions[portionIndex].days += getDayAsBytes(day);
			//}
		}
	}

	// Gates

	if (isVariationMunicipality()) {
		for (const gate of store.state.gateAnswers) {
			let days = 0;
			for (const day of gate.selectedDays) {
				days += getDayAsBytes(day);
			}
			postData.gates.push({
				gateId: gate.id,
				days: days
			});
		}
	}

	// Results
	const intake: IntakeAmount = getIntake();
	const intakeResult: IntakeResult = getIntakeResult();

	postData.result.foodResult.totalWeight = intake[Intake.FOOD];
	postData.result.foodResult.totalPortions = intake[Intake.FOOD_PORTIONS];
	postData.result.foodResult.conclusion = intakeResult[Intake.FOOD];
	postData.result.foodResult.resultText = getIntakeFeedback(Intake.FOOD, intake[Intake.FOOD_PORTIONS]);
	postData.result.drinkResult.totalWeight = intake[Intake.DRINKS];
	postData.result.drinkResult.totalPortions = intake[Intake.DRINKS_PORTIONS];
	postData.result.drinkResult.conclusion = intakeResult[Intake.DRINKS];
	postData.result.drinkResult.resultText = getIntakeFeedback(Intake.DRINKS, intake[Intake.DRINKS]);
	postData.result.totalConclusion = getTotalConclusion(intakeResult);

	//@ts-ignore
	window.submitData(postData);
}

function getTotalConclusion(result: IntakeResult): ResultCategory {
	if (result[Intake.FOOD] == ResultCategory.YELLOW || result[Intake.DRINKS] == ResultCategory.YELLOW) {
		return ResultCategory.YELLOW;
	}
	if (result[Intake.FOOD] == ResultCategory.RED && result[Intake.DRINKS] == ResultCategory.GREEN) {
		return ResultCategory.YELLOW;
	}
	if (result[Intake.FOOD] == ResultCategory.GREEN && result[Intake.DRINKS] == ResultCategory.RED) {
		return ResultCategory.YELLOW;
	}
	if (result[Intake.FOOD] == ResultCategory.GREEN && result[Intake.DRINKS] == ResultCategory.GREEN) {
		return ResultCategory.GREEN;
	}
	if (result[Intake.FOOD] == ResultCategory.RED && result[Intake.DRINKS] == ResultCategory.RED) {
		return ResultCategory.RED;
	}

	return ResultCategory.GREEN;
}
